import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { CallsApi, CrmApi } from '../../api/resources';
import RecordingPlayer from './RecordingPlayer';
import { TYPE_CLASS, TYPE_LABEL, fmtDuration } from './callUtils';
import { errorMessage } from '../../api/client';
import Can from '../../components/Can';
import Icon from '../../components/Icon';
import { formatCustomValue } from '../../components/DynamicFields';
import LeadFormModal from './LeadFormModal';
import FollowupFormModal from './FollowupFormModal';
import useCrmLookups, { StatusBadge, fmtDate } from './useCrmLookups';

export default function LeadDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const lookups = useCrmLookups();
  const [lead, setLead] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // 'edit' | 'followup'
  const [calls, setCalls] = useState(null);
  const canSeeCalls = ['crm.calls.view_all', 'crm.calls.view_own'].some(can);

  const load = useCallback(async () => {
    try {
      setLead(await CrmApi.leads.get(id));
    } catch (err) {
      setError(errorMessage(err, 'Could not load lead.'));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const loadCalls = useCallback(() => {
    if (canSeeCalls) CallsApi.leadCalls(id).then(setCalls).catch(() => setCalls([]));
  }, [id, canSeeCalls]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  const afterSave = () => {
    setModal(null);
    load();
    loadCalls();
  };

  if (error) return <div className="notice notice-error">{error}</div>;
  if (!lead) return <span className="skeleton" style={{ width: 240 }} />;

  // Custom-field definitions to label stored values (active ones only).
  const leadFields = lookups.leadFields;
  const followupFields = lookups.followupFields;

  return (
    <div>
      <div className="page-head">
        <div>
          <Link to="/crm/leads" className="muted">
            ← All leads
          </Link>
          <h1 className="page-title">
            {lead.name} <StatusBadge status={lead.status} />
          </h1>
          <p className="page-sub">
            {lead.phone}
            {lead.email ? ` · ${lead.email}` : ''}
          </p>
        </div>
        <div className="toolbar">
          {['whatsapp.chats.view_all', 'whatsapp.chats.view_own', 'whatsapp.chats.initiate'].some(can) && (
            <Link
              className="btn"
              to={`/whatsapp/chats?phone=${encodeURIComponent(lead.phone)}&name=${encodeURIComponent(lead.name)}`}
            >
              <Icon name="chat" size={16} />
              WhatsApp
            </Link>
          )}
          <Can permission="crm.leads.update">
            <button className="btn" disabled={!lookups.ready} onClick={() => setModal('edit')}>
              <Icon name="edit" size={16} />
              Edit
            </button>
          </Can>
          <Can permission="crm.followups.create">
            <button className="btn btn-primary" disabled={!lookups.ready} onClick={() => setModal('followup')}>
              <Icon name="plus" size={16} />
              Add follow-up
            </button>
          </Can>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <dl className="details">
          <div><dt>Company</dt><dd>{lead.company || '—'}</dd></div>
          <div><dt>Source</dt><dd>{lead.source || '—'}</dd></div>
          <div><dt>Assigned to</dt><dd>{lead.assignee?.name || 'Unassigned'}</dd></div>
          <div><dt>Last follow-up</dt><dd>{fmtDate(lead.last_followed_up_at)}</dd></div>
          <div><dt>Next follow-up</dt><dd>{fmtDate(lead.next_follow_up_at)}</dd></div>
          <div><dt>Registered</dt><dd>{fmtDate(lead.created_at)}</dd></div>
          {leadFields.map((f) => (
            <div key={f.id}>
              <dt>{f.label}</dt>
              <dd>{formatCustomValue(f, lead.custom_values[f.key])}</dd>
            </div>
          ))}
          {lead.notes && (
            <div className="wide"><dt>Notes</dt><dd>{lead.notes}</dd></div>
          )}
        </dl>
      </div>

      {canSeeCalls && calls && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-head">
            <strong>Phone calls</strong>
            <span className="muted">{calls.length} on this number</span>
          </div>
          {calls.length === 0 ? (
            <div className="empty">
              <strong>No calls yet</strong>
              Calls made from the Android app to {lead.phone} will appear here.
            </div>
          ) : (
            <div className="table-wrap">
              <table className="grid">
                <tbody>
                  {calls.map((c) => (
                    <tr key={c.id}>
                      <td>{fmtDate(c.call_timestamp)}</td>
                      <td><span className={TYPE_CLASS[c.call_type]}>{TYPE_LABEL[c.call_type]}</span></td>
                      <td>{fmtDuration(c.call_duration)}</td>
                      <td>{c.staff?.name || '—'}</td>
                      <td>{c.followup_id ? <span className="badge badge-ok">Follow-up logged</span> : <span className="muted">No follow-up yet</span>}</td>
                      <td>{c.recordings.map((r) => <RecordingPlayer key={r.id} recording={r} />)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Can permission="crm.followups.view">
        <div className="card">
          <div className="card-head">
            <strong>Follow-up history</strong>
          </div>
          {lead.followups.length === 0 ? (
            <div className="empty">
              <strong>No follow-ups yet</strong>
              Log the first call, message or visit.
            </div>
          ) : (
            <ul className="timeline">
              {lead.followups.map((f) => (
                <li key={f.id}>
                  <div className="timeline-head">
                    <span className="badge badge-plain">{f.type}</span>
                    {f.from_status?.id !== f.to_status?.id && f.from_status && (
                      <>
                        <StatusBadge status={f.from_status} /> →
                      </>
                    )}
                    <StatusBadge status={f.to_status} />
                    <span className="muted">
                      {fmtDate(f.followed_up_at)}
                      {f.user ? ` · ${f.user.name}` : ''}
                    </span>
                  </div>
                  {f.type === 'call' && (
                    f.call ? (
                      <div className="sub" style={{ color: 'var(--ok)' }}>
                        ✓ Call verified: {TYPE_LABEL[f.call.call_type].toLowerCase()}, {fmtDuration(f.call.call_duration)}
                        {f.call.recordings.map((r) => (
                          <span key={r.id} style={{ marginLeft: 8 }}><RecordingPlayer recording={r} /></span>
                        ))}
                      </div>
                    ) : (
                      <div className="sub">No call linked</div>
                    )
                  )}
                  {f.remarks && <p>{f.remarks}</p>}
                  {followupFields.map((cf) =>
                    f.custom_values[cf.key] !== undefined && f.custom_values[cf.key] !== '' ? (
                      <div key={cf.id} className="sub">
                        {cf.label}: {formatCustomValue(cf, f.custom_values[cf.key])}
                      </div>
                    ) : null
                  )}
                  {f.next_follow_up_at && (
                    <div className="sub">Next: {fmtDate(f.next_follow_up_at)}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Can>

      {modal === 'edit' && (
        <LeadFormModal lead={lead} lookups={lookups} onClose={() => setModal(null)} onSaved={afterSave} />
      )}
      {modal === 'followup' && (
        <FollowupFormModal lead={lead} lookups={lookups} onClose={() => setModal(null)} onSaved={afterSave} />
      )}
    </div>
  );
}
